import type { KindFeatures } from "../constants/process.constants";
import type { Field, ShallowField } from "./field.types";
import type { Step, ShallowStep } from "./step.types";
import type { LCDataDef } from "./lcData.types";
import type { BulkAction, BulkActionRaw } from "./bulkAction.types";
import type { ConditionGroupMap, ConditionMap, RuleMap } from "./rules.types";
import type { Swimlane, ShallowSwimlane } from "./swimlane.types";
import type { DefaultThread } from "./features.types";
import type { ECA } from "./eca.types";
import type { ZodRawProcess } from "@certa/queries/schemas/studio/deployableJSON.schema";

export const SAVE_ID_KEY = "process.__saveId";
export type ProcessKindProps = {
  /**
   * Tag of status that needs to be set by default when a process is spawned
   * from this process kind.
   */
  defaultStatus: string;

  /**
   * Tags of statuses that is applicable for this particular process kind.
   * Within the process' lifecycle only these statuses can be applied to it.
   */
  availableStatuses: string[];
  /**
   * Name of the process that's visible to the end user within the
   * process types dropdown. This can be changed.
   */
  name: string;
  /**
   * Writeable only once, and is derived from the very first value of
   * `name`. Name can be changed later, but we will not regenerate tag
   * from name ever again.
   */
  tag: string;
  permissions: {
    /**
     * Table like UI where user picks up the static/dynamic group and a multi-select
     * to pick their respective features.
     *
     * This does not defines access.
     */
    staticGroups: Record<string, KindFeatures[] | string[]>;
    dynamicGroups: Record<string, KindFeatures[] | string[]>;
  };

  /**
   * Defines - Which static group may be conditionally able to view this process.
   *
   * Use case: Let's say G1 has always-can-see access to this process, but G2 may
   * only be able to see this process only after a certain condition is met, within
   * the process. Now since, in 2020 UI, we only used to show static groups that
   * always-can-see this process, this prop is introduced to extend that functionality
   * such that these groups may also see the option to see processes of this type.
   * They'll see a blank list, if they actually do not have access to any process instances
   * within that, but when they do, they'll only be able to see those specific processes.
   * (instead of showing all instances of that process -- as it happens with staticGroup )
   */
  visibleToStaticGroups?: string[];
  objectTypes?: string[];
};
export type ProcessProps = {
  id: string; // number as string
  kind: ProcessKindProps;

  /**
   * Default step for any user (who may not have a default step defined as per his groups)
   * where they will land when they open the workflow.
   */
  defaultStepTags?: string[];

  /**
   * Defines the default step tag, according to user groups. This takes precedence over
   * the defaultStepTag.
   */
  defaultStepTagForGroups?: Record<string, string>;

  /**
   * Defines - Who can see this process
   */
  staticGroups?: string[];
  dynamicGroupNames?: string[];

  /**
   * `CREATOR` is a system reserved dynamic group that allows only the creator of the
   * process to be able to see the processes, exclusively.
   *
   * This sections defines the permissions that they get over their exclusive process.
   */
  creatorPermissions?: string[]; // TODO: change string[] to Permission[];

  /**
   * Defines the permission, per dynamic group, whenever that dynamic group
   * gets created within the processes' lifecycle.
   */
  dynamicGroupNamesWithPerm?: Record<string, string[]>;

  accessGroupConstraints?: string[];

  /**
   * Whenever a user mentions another user within the "comments" section, this
   * template (tag) will be used to send the email notification to the tagged user.
   */
  mentionsTemplate?: string;

  lcData?: LCDataDef[];

  // pdfConfiguration // TODO:

  // updateInProgressWorkflows // TODO:

  /**
   * Defines field tags of integration fields, which will be executed automatically when
   * the process is instantiated.
   * Usually used with Bulk Import functionality.
   */
  autoIntegrationCallFieldTags?: string[];
  /**
   * Defines the field tags of integration fields, which will be executed automatically when
   * the process is archived.
   */
  onDeleteIntegrationFieldTags?: string[];

  nodeOrder?: string[][];
  pdfConfigurations?: any[]; // JSON BOX for now, but has to be array

  relatedTypes?: string[];
  /**
   * Stores the sequence of swimlanes
   */
  swimlaneSequence: string[];
  eventRuleSequence: string[]; // Array of event rule IDs in order
  stateRuleSequence: string[]; // Array of state rule IDs in order
  bulkActions?: string[];
  bulkActionsRaw?: BulkActionRaw[];
  defaultThreads?: Record<string, DefaultThread>;
  modelReqFields?: string[];
  contentType?: "user";
  childMappings?: Record<
    string,
    {
      child_workflow_kind: string;
      child_field_mapping: Record<string, string>;
    }
  >;
  onCreateWorkflow?: ZodRawProcess["on_create_workflow"];

  __modifiedAt: number; // Timestamp to denote when process record was modified
  __createdAt: number; // Timestamp to denote when process was created
  __branchTimestamp: number; // This timestamp is set when process is created, a new branch is created, or other branch process is merged into it

  __lastMigration?: string;
  __saveId: string;
};

export type Process = ECA & {
  process: ProcessProps;
  swimlanes: Record<string, Swimlane>;
  steps: Record<string, Step>;
  fields: Record<string, Field>;
  rules: RuleMap;
  conditions: ConditionMap;
  conditionGroups: ConditionGroupMap;
  bulkActions: Record<string, BulkAction>;
};

export type ShallowProcess = {
  process: ProcessProps | null;
  swimlanes: ShallowSwimlane[];
  steps: ShallowStep[];
  fields: ShallowField[];
};

export enum RelatedProcessFlowType {
  CHILD_TO_PARENT = "child_to_parent",
  PARENT_TO_CHILD = "parent_to_child"
}

export enum ProcessLayoutMode {
  STRUCTURE_VIEW = "details",
  FLOW_VIEW = "flow_legacy",
  BUSINESS_FLOW_VIEW = "flow",
  RULES_VIEW = "rules",
  PREVIEW_VIEW = "preview",
  RECORDS_VIEW = "records"
}
